<template>
  <div class="chart-container">
    <line-chart v-if="loaded" :chartData="chartData" :chartOptions="chartOptions"/>
    <div v-if="loaded">
      <div class="legend-heading">
        <b>{{$t('chart.legend')}}</b>
        <div>
          <div>{{$t('chart.leftAxis')}}</div>
          <div>{{$t('chart.rightAxis')}}</div>
        </div>
      </div>
      <div>
        <div v-for="sid in this.$store.getters['searchResults'].map(p => p.parcel_foreign_id)" :key="sid">
          <div>
            {{$t('search.parcel')}} № {{sid}}
          </div>
          <div class="legend-entry">
            <div>
              <input class="color-p" @change="onColorChange($event, sid, 'left')" v-model="legendObject[sid].leftColor" type="color">
            </div>
            <div>
              <div class="select is-small">
                <select v-model="legendObject[sid].left" @change="onChartLineChange()" class="has-background-grey has-text-light">
                  <option v-bind:style="lastUpdate && legendObject[sid].right !== '-' && legendObject[sid].right === line ? 'visibility: hidden;' : 'background: visible;'" v-for="line in chartLines" :value="line" :key="sid + line"> {{ line }} </option>
                </select>
              </div>
            </div>
            <div>
              <input class="color-p" @change="onColorChange($event, sid, 'right')" v-model="legendObject[sid].rightColor" type="color">
            </div>
            <div>
              <div class="select is-small">
                <select v-model="legendObject[sid].right" @change="onChartLineChange()" class="has-background-grey has-text-light">
                  <option v-bind:style="lastUpdate && legendObject[sid].left !== '-' &&  legendObject[sid].left === line ? 'visibility: hidden;' : 'background: visible;'" v-for="line in chartLines" :value="line" :key="sid + line"> {{ line }} </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <b-button @click="applyLayers" type="button is-dark has-background-black-bis">{{$t('chart.applySelection')}}</b-button>
      </div>
    </div>
  </div>
</template>

<script>

import log from 'util/log'
import moment from 'moment'
import LineChart from './Chart.vue'
import { i18n } from '../../i18n.js' 

export default {
  name: 'ChartContainerComponent',
  components: { 
    LineChart
    },
  props: {

  },
  data () {
    return {
      loaded: false,
      chartData: null,
      chartLines: [],
      lastUpdate: moment(),
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const label = i18n.t("search.parcel") + " " + context[0].dataset.id + ": " + context[0].label
                return label
              }
            }
          }
        },
        scales: {
          left: {
            type: "linear",
            title: {
              display: true,
              text: 'left',
              color: 'white'
            },
            ticks: {
              color: 'white'
            },
            display: true,
            position: "left"
          },
          right: {
            type: "linear",
            title: {
              display: true,
              text: 'right',
              color: 'white'
            },
            ticks: {
              color: 'white'
            },
            display: true,
            position: "right"
          },
          x: {
            ticks: { 
              color: 'white'
            }
          }
        },
        onClick: (e, points) => {
          if (points.length) {
            const lineId = points[0].element.$context.dataset.lineId
            if (!this.$store.getters['config'].chart.lines.filter(l => l.id === lineId)[0].params.click)
              return 0
            
            const requestDate = points[0].element.$context.raw.requestDate
            const dates = this.chartData.datasets.filter(l => l.lineId === lineId)[0].data.map(d => d.requestDate)
            const tsGroup = this.$store.getters['config'].chart.lines.filter(line => line.id === lineId)[0].group
            const parcelId = points[0].element.$context.dataset.id

            this.$store.dispatch('chartImageryDate', requestDate)
            this.$store.dispatch('chartImageryDates', dates)
            this.$store.dispatch('chartImageryTsGroup', tsGroup)
            this.$store.dispatch('chartImageryParcelId', parcelId)
          }
        }
      }
    }
  },
  computed: {
    legendObject () {
      const leftColors = this.$store.getters['config'].chart.leftLineColors
      const rightColors = this.$store.getters['config'].chart.rightLineColors
      const selectedIds = this.$store.getters['searchResults'].map(p => p.parcel_foreign_id)

      var model = {}
      selectedIds.forEach((id, index) => {
        model[id] = {
          left: index === 0 ? i18n.t("chart.legend." + this.$store.getters['config'].chart.tsDefault) : '-',
          right: '-',
          leftColor: index < leftColors.length ? leftColors[index] : leftColors[index - leftColors.length],
          rightColor: index < rightColors.length ? rightColors[index] : rightColors[index - rightColors.length]
        }
      })
      return model
    },
  },
  mounted () {
    this.loaded = false
    this.fillData()
  },
  methods: {
    applyLayers () {
      const firstParcelId = this.$store.getters['searchResults'][0].parcel_foreign_id

      for (const [parcel_f_id, value] of Object.entries(this.legendObject)) {
        if (parcel_f_id !== firstParcelId) {
          value.left = this.legendObject[firstParcelId].left
          value.right = this.legendObject[firstParcelId].right
        }
      }
      this.onChartLineChange()
    },
    onColorChange (e, id, side) {
      var dataset = this.chartData.datasets.filter(d => d.id === id && d.yAxisID === side)[0]
      
      if (dataset) {
        dataset.backgroundColor = e.target.value
        dataset.borderColor = e.target.value
        if (dataset.click === false)
          dataset.pointBorderColor = e.target.value
      }
    },
    onChartLineChange () {
      var newDataset = []

      for (const [parcel_f_id, value] of Object.entries(this.legendObject)) {
        var ldataset = this.$store.getters['chartDataStorage'].datasets.filter(d => {
          if (d.id === parcel_f_id && d.label === value.left) {
            d.backgroundColor = value.leftColor
            d.borderColor = value.leftColor
            if (d.click === false)
              d.pointBorderColor = value.leftColor
            d.yAxisID = 'left'
            return true
          }
          if (d.id === parcel_f_id && d.label === value.right) {
            d.backgroundColor = value.rightColor
            d.borderColor = value.rightColor
            if (d.click === false)
              d.pointBorderColor = value.rightColor
            d.yAxisID = 'right'
            return true
          }
          return false
        })
        newDataset = newDataset.concat(ldataset)
      }

      this.chartData.datasets = newDataset
      this.updateAxesLabels()
      this.lastUpdate = moment()
    },
    updateAxesLabels () {
      var leftTitle = this.chartData.datasets.filter(d => d.yAxisID === 'left').map(l => l.label)
      var rightTitle = this.chartData.datasets.filter(d => d.yAxisID === 'right').map(l => l.label)

      this.chartOptions.scales.left.title.text = [... new Set(leftTitle)].join(', ')
      this.chartOptions.scales.right.title.text = [... new Set(rightTitle)].join(', ')
    },
    fillData () {
      this.loaded = false

      const selectedIds = this.$store.getters['searchResults'].map(i => i.parcel_foreign_id)
      this.$store.dispatch('loadChartData', selectedIds.shift()).then((result) => {
        if (result) {
          this.chartData = {
            labels: this.$store.getters['chartDataStorage'].timeLabels,
            datasets: []
          }
          this.chartLines = ['-'].concat([... new Set(result.map(d=>d.label))])
          this.loaded = true
          this.onChartLineChange()
        }
      })
      selectedIds.forEach(id => {
        this.$store.dispatch('loadChartData', id)
      })
      
    }
  }
}
</script>

<style lang="stylus">

.chart-container
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  >div:nth-of-type(1)
    height: 100%;
    width: 100%;
    margin-right: 1rem;
    padding-right: 0.5rem;
    border-right: 1px solid #7a7a7a;
  >div:nth-of-type(2) 
    height: 100%;
    display: flex;
    flex-direction: column;
    align-content: center;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: stretch;
    flex-basis: content;
    flex-shrink: 0;
    flex-grow: 0;
    >div:nth-of-type(2)
      overflow: hidden visible;
      margin-bottom: 5px;
      height: 100%

.legend-entry
  display: flex;
  flex-direction: row;
  margin-bottom: 0.5rem;

  input.color-p
    width: 1.75rem;
    height: 100%;

  >div:nth-child(odd)
    flex-basis: auto;
  >div:nth-child(even)
    flex-basis: 40%;
    margin-right: 1rem;

.legend-heading
  text-align: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid white;

  >div
    display: flex;
    flex-direction: row;
    justify-content: space-around;
</style>
